/**
 * 📍 Native Location Tracking Service
 *
 * ⚠️  DEPRECATED for workout GPS tracking (April 2026).
 * GPS tracking for workouts is now handled directly in UnifiedWorkoutTimer.native.tsx
 * with accuracy filtering, spike detection, warm-up buffering, and rolling-window pace.
 * See @/utils/gpsFilterUtils.ts and @/utils/gpsTypes.ts for the shared GPS pipeline.
 *
 * STILL USED FOR: GPS permission management (checkPermissionStatus, requestGPSPermission,
 * openAppSettings) by the ProfileDrawer settings screen.
 *
 * Original: JAN 26, 2026 — GPS permission management (MISSION 20 COMPLIANT)
 * - WMA: Browser Geolocation API (pure web)
 * - iOS: CoreLocation via expo-location (conditional import)
 * - Android: Location Services via expo-location (conditional import)
 */

import { storage } from './storage';

// 🚨 MISSION 20: Conditional imports for react-native modules
// NEVER import Platform/Location at top level - breaks web builds!
let Platform: any = null;
let Location: any = null;
let Linking: any = null;
let IntentLauncher: any = null;

// Detect if we're on web (no react-native available)
const isWeb = typeof window !== 'undefined' && !window.ReactNativeWebView;

if (!isWeb) {
  // Only load react-native modules on native platforms
  try {
    const RN = require('react-native');
    Platform = RN.Platform;
    Linking = RN.Linking;
    Location = require('expo-location');
    
    try {
      IntentLauncher = require('expo-intent-launcher');
    } catch (e) {
      console.warn('expo-intent-launcher not available');
    }
  } catch (e) {
    console.warn('React Native modules not available');
  }
}

interface LocationPoint {
  latitude: number;
  longitude: number;
  altitude: number | null;
  accuracy: number | null;
  speed: number | null;
  timestamp: number;
}

interface WorkoutRoute {
  startTime: number;
  endTime?: number;
  points: LocationPoint[];
  totalDistance: number; // meters
  isTracking: boolean;
}

class NativeLocationTrackingService {
  private currentRoute: WorkoutRoute | null = null;
  private locationSubscription: any = null;
  private lastPoint: LocationPoint | null = null;

  /**
   * 🆕 Check current GPS permission status (ALL PLATFORMS)
   * Returns: 'granted' | 'denied' | 'prompt' | 'unavailable'
   */
  async checkPermissionStatus(): Promise<'granted' | 'denied' | 'prompt' | 'unavailable'> {
    // WMA (Browser) - Use navigator.permissions API
    if (isWeb) {
      try {
        if (!navigator.permissions) {
          return 'unavailable';
        }
        const permission = await (navigator as any).permissions.query({ name: 'geolocation' });
        return permission.state as 'granted' | 'denied' | 'prompt';
      } catch (error) {
        console.error('Browser permissions API error:', error);
        return 'unavailable';
      }
    }

    // iOS/Android (Native) - Use expo-location
    if (!Location) {
      return 'unavailable';
    }

    try {
      const { status } = await Location.getForegroundPermissionsAsync();
      
      switch (status) {
        case Location.PermissionStatus?.GRANTED:
          return 'granted';
        case Location.PermissionStatus?.DENIED:
          return 'denied';
        case Location.PermissionStatus?.UNDETERMINED:
          return 'prompt';
        default:
          return 'unavailable';
      }
    } catch (error) {
      console.error('Failed to check permission status:', error);
      return 'unavailable';
    }
  }

  /**
   * 🆕 Request GPS permission (ALL PLATFORMS)
   */
  async requestGPSPermission(): Promise<boolean> {
    // WMA (Browser) - Use geolocation API to trigger prompt
    if (isWeb) {
      try {
        if (!navigator.geolocation) {
          console.error('Geolocation API not available');
          return false;
        }

        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              console.log('✅ WMA: GPS permission granted');
              resolve(true);
            },
            (error) => {
              console.warn('WMA: GPS permission denied:', error);
              resolve(false);
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            }
          );
        });
      } catch (error) {
        console.error('WMA: Failed to request GPS permission:', error);
        return false;
      }
    }

    // iOS/Android (Native) - Use expo-location
    if (!Location) {
      console.error('Location module not available');
      return false;
    }

    try {
      // Every OS location request goes through the shared flow (Play prominent-disclosure
      // compliance, 2026-09-11). Lazy require honours this file's MISSION 20 rule.
      const { ensureWorkoutLocationPermission } = require('./locationPermissionFlow');
      const { fgStatus } = await ensureWorkoutLocationPermission({ surface: 'timer' });
      return fgStatus === 'granted';
    } catch (error) {
      console.error('Native: Failed to request GPS permission:', error);
      return false;
    }
  }

  /**
   * 🆕 Open platform settings (iOS/Android only, WMA returns false)
   */
  async openAppSettings(): Promise<boolean> {
    // WMA (Browser) - Cannot open browser settings programmatically
    if (isWeb) {
      console.log('WMA: Cannot open browser settings programmatically');
      return false;
    }

    // iOS - Opens Settings.app to Coach Cedar page
    if (Platform?.OS === 'ios' && Linking) {
      try {
        const canOpen = await Linking.canOpenURL('app-settings:');
        if (canOpen) {
          await Linking.openSettings();
          console.log('✅ iOS: Opened Settings.app');
          return true;
        }
        return false;
      } catch (error) {
        console.error('iOS: Failed to open Settings.app:', error);
        return false;
      }
    }

    // Android - Opens Settings → Apps → Coach Cedar
    if (Platform?.OS === 'android' && IntentLauncher) {
      try {
        await IntentLauncher.startActivityAsync(
          IntentLauncher.ActivityAction.APPLICATION_DETAILS_SETTINGS,
          { data: 'package:com.coachcedar.app' }
        );
        console.log('✅ Android: Opened app settings');
        return true;
      } catch (error) {
        console.error('Android: Failed to open app settings:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Request location permissions (legacy method - kept for backward compatibility)
   */
  async requestPermissions(): Promise<boolean> {
    if (isWeb) {
      console.log('📍 Location tracking uses browser API on web');
      return false;
    }

    if (!Location) {
      console.error('Location module not available');
      return false;
    }

    try {
      // The shared flow owns the disclosure and every OS location request (Play
      // prominent-disclosure compliance, 2026-09-11). Lazy require: MISSION 20.
      const { ensureWorkoutLocationPermission } = require('./locationPermissionFlow');
      const { fgStatus } = await ensureWorkoutLocationPermission({ surface: 'timer' });
      return fgStatus === 'granted';
    } catch (error) {
      console.error('Failed to request location permissions:', error);
      return false;
    }
  }

  /**
   * Start tracking a workout route
   */
  async startTracking(): Promise<boolean> {
    if (isWeb) return false;
    if (!Location) {
      console.error('Location module not available');
      return false;
    }

    try {
      // Check permissions
      const { status } = await Location.getForegroundPermissionsAsync();
      if (status !== Location.PermissionStatus?.GRANTED) {
        const granted = await this.requestPermissions();
        if (!granted) return false;
      }

      // Initialize route
      this.currentRoute = {
        startTime: Date.now(),
        points: [],
        totalDistance: 0,
        isTracking: true,
      };

      // Start watching location
      this.locationSubscription = await Location.watchPositionAsync(
        {
          accuracy: Location.Accuracy?.BestForNavigation,
          timeInterval: 1000, // Update every 1 second
          distanceInterval: 5, // Update every 5 meters
        },
        (location: any) => {
          this.handleLocationUpdate(location);
        }
      );

      console.log('✅ GPS tracking started');
      return true;
    } catch (error) {
      console.error('Failed to start tracking:', error);
      return false;
    }
  }

  /**
   * Handle location update
   */
  private handleLocationUpdate(location: any) {
    if (!this.currentRoute) return;

    const newPoint: LocationPoint = {
      latitude: location.coords.latitude,
      longitude: location.coords.longitude,
      altitude: location.coords.altitude,
      accuracy: location.coords.accuracy,
      speed: location.coords.speed,
      timestamp: location.timestamp,
    };

    // Calculate distance from last point
    if (this.lastPoint) {
      const distance = this.calculateDistance(this.lastPoint, newPoint);
      this.currentRoute.totalDistance += distance;
    }

    this.currentRoute.points.push(newPoint);
    this.lastPoint = newPoint;

    console.log(`📍 Location update: ${newPoint.latitude}, ${newPoint.longitude}, Distance: ${this.currentRoute.totalDistance.toFixed(0)}m`);
  }

  /**
   * Calculate distance between two points (Haversine formula)
   */
  private calculateDistance(point1: LocationPoint, point2: LocationPoint): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRad(point2.latitude - point1.latitude);
    const dLon = this.toRad(point2.longitude - point1.longitude);
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(point1.latitude)) *
      Math.cos(this.toRad(point2.latitude)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Stop tracking and return route
   */
  async stopTracking(): Promise<WorkoutRoute | null> {
    if (isWeb) return null;

    if (this.locationSubscription) {
      this.locationSubscription.remove();
      this.locationSubscription = null;
    }

    if (this.currentRoute) {
      this.currentRoute.endTime = Date.now();
      this.currentRoute.isTracking = false;
      
      const route = { ...this.currentRoute };
      
      // Save route to storage
      await this.saveRoute(route);
      
      // Reset
      this.currentRoute = null;
      this.lastPoint = null;
      
      console.log('✅ GPS tracking stopped, Total distance:', route.totalDistance.toFixed(0), 'm');
      return route;
    }

    return null;
  }

  /**
   * Pause tracking (keep route data, stop updates)
   */
  pauseTracking(): void {
    if (this.locationSubscription) {
      this.locationSubscription.remove();
      this.locationSubscription = null;
    }
    
    if (this.currentRoute) {
      this.currentRoute.isTracking = false;
    }
    
    console.log('⏸️  GPS tracking paused');
  }

  /**
   * Resume tracking
   */
  async resumeTracking(): Promise<boolean> {
    if (!this.currentRoute) {
      console.warn('No route to resume');
      return false;
    }

    return await this.startTracking();
  }

  /**
   * Get current tracking status
   */
  getTrackingStatus(): {
    isTracking: boolean;
    distance: number;
    duration: number;
    pointCount: number;
  } | null {
    if (!this.currentRoute) return null;

    const duration = this.currentRoute.endTime
      ? this.currentRoute.endTime - this.currentRoute.startTime
      : Date.now() - this.currentRoute.startTime;

    return {
      isTracking: this.currentRoute.isTracking,
      distance: this.currentRoute.totalDistance,
      duration,
      pointCount: this.currentRoute.points.length,
    };
  }

  /**
   * Save route to persistent storage
   */
  private async saveRoute(route: WorkoutRoute): Promise<void> {
    try {
      const routesJson = await storage.getItem('workout_routes');
      const routes = routesJson ? JSON.parse(routesJson) : [];
      routes.push(route);
      await storage.setItem('workout_routes', JSON.stringify(routes));
      console.log('✅ Route saved to storage');
    } catch (error) {
      console.error('Failed to save route:', error);
    }
  }

  /**
   * Get current location (one-time fetch)
   */
  async getCurrentLocation(): Promise<LocationPoint | null> {
    if (isWeb) return null;
    if (!Location) return null;

    try {
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy?.High,
      });

      return {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        altitude: location.coords.altitude,
        accuracy: location.coords.accuracy,
        speed: location.coords.speed,
        timestamp: location.timestamp,
      };
    } catch (error) {
      console.error('Failed to get current location:', error);
      return null;
    }
  }
}

// Export singleton instance
export const nativeLocationTrackingService = new NativeLocationTrackingService();

// Export types
export type { LocationPoint, WorkoutRoute };

